import { useContext, useMemo, useState, type ReactNode } from "react";
import ModifiedCellsContext from "../context/ModifiedCellsContext";
import { VersionChangesContext } from "../context/VersionChangesContext";
import { versionChangesBaseLabel } from "@/services/roster/versionChanges";
import { Button } from "@/components/ui/button";
import { useVersionChanges } from "../hook/useVersionChanges";
import "@/pages/roster-view/components/roster-reissue.css";
import {
  RosterVersionChangesDialog,
  VersionChangesSummary,
} from "./RosterVersionChangesDialog";
export function VersionChangesProvider({
  groupId,
  scheduleId,
  enabled,
  children,
}: {
  readonly groupId: string;
  readonly scheduleId: string;
  readonly enabled: boolean;
  readonly children: ReactNode;
}) {
  const query = useVersionChanges(groupId, scheduleId, enabled);
  const { modifiedCells } = useContext(ModifiedCellsContext);
  const cells = useMemo(
    () =>
      new Map(
        (enabled && !query.isError ? (query.data?.cells ?? []) : [])
          .filter(
            (cell) =>
              !modifiedCells.has(
                `${cell.nurse_id}-${Number(cell.date.slice(-2)) - 1}`
              )
          )
          .map((cell) => [
            `${cell.nurse_id}:${Number(cell.date.slice(-2))}`,
            cell,
          ])
      ),
    [enabled, query.data, query.isError, modifiedCells]
  );
  return (
    <VersionChangesContext.Provider value={cells}>
      {children}
    </VersionChangesContext.Provider>
  );
}

export function RosterVersionChangesBar({
  groupId,
  scheduleId,
}: {
  readonly groupId: string;
  readonly scheduleId: string;
}) {
  const query = useVersionChanges(groupId, scheduleId);
  const [open, setOpen] = useState(false);
  const { modifiedCells } = useContext(ModifiedCellsContext);
  if (!scheduleId || !groupId) return null;
  if (
    query.isPending ||
    (!query.isError && (!query.data || query.data.summary.total_cells === 0))
  )
    return null;
  return (
    <div
      className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-3 py-1 ${query.isError ? "bg-white" : "roster-reissue-bar"}`}
      data-testid="roster-version-changes-bar"
    >
      {query.isError ? (
        <>
          <p role="alert" className="text-xs text-muted-foreground">
            변경 내역을 불러오지 못했습니다.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            다시 시도
          </Button>
        </>
      ) : (
        query.data && (
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <p className="text-xs font-medium text-muted-foreground">
                {versionChangesBaseLabel(query.data)}
              </p>
              <span className="rounded bg-[var(--roster-reissue)] px-2 py-0.5 text-[11px] font-bold text-white">
                버전 비교
              </span>
              <VersionChangesSummary data={query.data} />
              <span className="text-[11px] text-muted-foreground">
                {modifiedCells.size > 0
                  ? "수정한 칸의 변경 표시는 저장 후 갱신됩니다"
                  : "저장된 내용 기준"}
              </span>
            </div>
            <Button
              className="ml-auto h-7 rounded-full border-[var(--roster-reissue)] bg-background px-2 text-xs font-semibold text-[var(--roster-reissue)] hover:bg-[var(--roster-reissue-bg)] hover:text-[var(--roster-reissue)]"
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
            >
              변경 내역 {query.data.summary.total_cells}건
              <span aria-hidden="true">›</span>
            </Button>
          </>
        )
      )}
      {open && (
        <RosterVersionChangesDialog
          scheduleId={scheduleId}
          groupId={groupId}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
